import React from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  MenuItem,
  Theme,
  Typography,
} from '@material-ui/core';
import { Field } from 'react-final-form';
import { AutoFocusedForm, notEmpty, TextField, SelectField, moreThanZero } from 'components';
import { MutableButton } from 'ui/MutableButton';
import { RedButton } from 'components/Buttons';
import { useQuestForm } from './useQuestForm';
import { Quest, QuestType, QuestFormValues, getQuestTypeLabel } from './types';
import { Gender, useCitiesQuery } from '../../../../../../schema';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: 500,
    },
    actions: {
      padding: theme.spacing(2),
      justifyContent: 'flex-end',
    },
    accessButton: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.success.dark,
      '&:hover': {
        color: theme.palette.success.dark,
        backgroundColor: theme.palette.success.light,
      },
    },
    sectionTitle: {
      fontWeight: 500,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    typeLabel: {
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius,
    },
  })
);

type Props = {
  seasonId: string;
  quest?: Quest | null;
  questType: QuestType;
  onClose: () => void;
};

export const QuestForm = ({ seasonId, quest, questType, onClose }: Props) => {
  const classes = useStyles();
  const { initialValues, onSubmit, loading, isEdit } = useQuestForm({
    seasonId,
    quest,
    questType,
    onClose,
  });

  const { data: citiesData } = useCitiesQuery();
  const cities = citiesData?.cities?.nodes;

  const title = isEdit ? 'Редактирование задания' : 'Создание задания';
  const submitButtonText = isEdit ? 'Сохранить' : 'Создать';

  return (
    <AutoFocusedForm<QuestFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, valid }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card className={classes.root}>
              <CardHeader title={title} />
              <Divider />
              <CardContent>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Информация о задании
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      name="title"
                      label="Заголовок"
                      component={TextField}
                      type="text"
                      validate={notEmpty}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="description"
                      label="Описание"
                      component={TextField}
                      type="text"
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Тип задания
                    </Typography>
                    <Typography variant="body1" className={classes.typeLabel}>
                      {getQuestTypeLabel(questType)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="rewardAmount"
                      label="Вознаграждение"
                      component={TextField}
                      type="number"
                      validate={moreThanZero}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Настройки задания
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field name="city" label="Город подопечных" component={SelectField}>
                      <MenuItem value="">
                        <em>Не выбран</em>
                      </MenuItem>
                      {cities?.map(({ name, id }) => (
                        <MenuItem key={id} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field name="gender" label="Пол подопечных" component={SelectField}>
                      <MenuItem value="">
                        <em>Не выбран</em>
                      </MenuItem>
                      <MenuItem value={Gender.Male}>Мужской</MenuItem>
                      <MenuItem value={Gender.Female}>Женский</MenuItem>
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="minimumCount"
                      label="Минимальное количество"
                      component={TextField}
                      type="number"
                      placeholder="Не ограничено"
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions className={classes.actions}>
                <MutableButton
                  className={classes.accessButton}
                  loading={loading}
                  color="primary"
                  disabled={!valid}
                  size="medium"
                  type="submit"
                  variant="contained"
                >
                  {submitButtonText}
                </MutableButton>
                <RedButton
                  color="primary"
                  size="medium"
                  type="button"
                  variant="contained"
                  onClick={onClose}
                >
                  Отменить
                </RedButton>
              </CardActions>
            </Card>
          </form>
        );
      }}
    />
  );
};
